import React, { useContext } from "react";

import AssessmentIcon from "@mui/icons-material/Assessment";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import HistoryIcon from "@mui/icons-material/History";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import SchemaIcon from "@mui/icons-material/Schema";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  Divider,
  Link as ExternalLink,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { Link } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  PERISCOPE_USER_GUIDE_LINK,
  timezonesToDisplay,
} from "../../../constants/index";
import { PermissionsContext } from "../../../context/permissions-context";
import FlagIcon from "./flag-icon";
import TimezoneClock from "./timezone-clock";

const NavList = () => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  // Show loading skeleton while permissions are being fetched
  if (permissions.isPermissionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} />
      </Box>
    );
  }

  const isPeriscopeAdmin = hasPermissions([
    ActionType.CREATE_GROUP,
    ActionType.ADD_ACTION,
  ]);
  const isSeoTemplateUser = hasPermissions([
    ActionType.UPLOAD_TEMPLATE_TO_CROWDIN,
  ]);
  const isSupportManager = hasPermissions([ActionType.UPDATE_USER_ADMIN_FLAG]);
  const isWorkflowUser =
    hasPermissions([ActionType.LIST_SUPPORT_WORKFLOW_EXECUTIONS]) ||
    hasPermissions([ActionType.LIST_SUBSCRIPTIONS_WORKFLOW_EXECUTIONS]);
  const isMarketplaceUser = hasPermissions([
    ActionType.LIST_MARKETPLACE_PRODUCT_ATTR_GEN_JOBS,
    ActionType.ADD_MARKETPLACE_PRODUCT_ATTR_GEN_JOB,
    ActionType.GET_MARKETPLACE_PRODUCT_ATTR_JOB_RESULT,
  ]);
  const isRetroShareUser = hasPermissions([ActionType.LIST_RETROSHARE]);

  const displayAdminItems = (isAdmin: boolean) => {
    if (isAdmin) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/settings"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Admin" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    return;
  };

  const displaySupportManagerItems = (isAdmin: boolean) => {
    if (isAdmin) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/admin-tools"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <HomeRepairServiceIcon />
              </ListItemIcon>
              <ListItemText primary="Support Tools" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    return;
  };

  const displaySeoTemplates = (isSeoUser: boolean) => {
    if (isSeoUser) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/seo-templates"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <TranslateIcon />
              </ListItemIcon>
              <ListItemText primary="SEO Templates" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    return;
  };

  const displayWorkflows = (isWorkflowUser: boolean) => {
    if (isWorkflowUser) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/workflows"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <SchemaIcon />
              </ListItemIcon>
              <ListItemText primary="Workflows" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    return;
  };

  const displayMarketplace = (isMarketplaceUser: boolean) => {
    if (isMarketplaceUser) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/marketplace"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <StoreIcon />
              </ListItemIcon>
              <ListItemText primary="Marketplace" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    return;
  };

  const displayRetroShare = (isRetroShareUser: boolean) => {
    if (isRetroShareUser) {
      return (
        <>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/retroshare"
              className="remove-visited-link-style"
            >
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>
              <ListItemText primary="Retroshares" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
  };

  return (
    <React.Fragment>
      {timezonesToDisplay.map((item, index) => (
        <ListItem key={index}>
          <ListItemIcon>
            <FlagIcon countryCode={item.code} />
          </ListItemIcon>
          <TimezoneClock timezone={item.timezone} dataTestId="timezone-clock" />
        </ListItem>
      ))}
      {/* To compensate for parent list component top padding */}
      <div style={{ marginBottom: 8 }} />
      <Divider />

      <ListItem>
        <ListItemButton
          component={Link}
          to="/"
          className="remove-visited-link-style"
        >
          <ListItemIcon>
            <SearchIcon />
          </ListItemIcon>
          <ListItemText primary="Search" />
        </ListItemButton>
      </ListItem>

      {/* Reports section - sudo history, also other audit logs to be surfaced here once implemented*/}
      <ListItem>
        <ListItemButton
          component={Link}
          to="/reports"
          className="remove-visited-link-style"
        >
          <ListItemIcon>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText primary="Reports" />
        </ListItemButton>
      </ListItem>

      <ListItem>
        <ListItemButton
          component={ExternalLink}
          href={PERISCOPE_USER_GUIDE_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="remove-visited-link-style"
        >
          <ListItemIcon>
            <HelpOutlineOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Help" />
        </ListItemButton>
      </ListItem>

      {displaySupportManagerItems(isSupportManager)}

      {/* SEO Template Translations - only those with crowdin upload action access will see on navbar*/}
      {displaySeoTemplates(isSeoTemplateUser)}

      {/* Workflows - only those with workflow access will see on navbar*/}
      {displayWorkflows(isWorkflowUser)}

      {/* Marketplace - only those with marketplace access will see on navbar */}
      {displayMarketplace(isMarketplaceUser)}

      {/* Retroshares - will only display if you have permissions for LIST_RETROSHARE */}
      {displayRetroShare(isRetroShareUser)}

      {/* Admin settings - only those with admin action access will see on navbar*/}
      {displayAdminItems(isPeriscopeAdmin)}
    </React.Fragment>
  );
};
export default NavList;
